import React, { useState } from 'react';

import { Box, Button, Icon, Link, Text } from '@betterment/samba-react';
import { Flex } from '@betterment/style-closet-react';

import { ExportBanner } from './TransactionExport/Banners';
import TransactionExportModal from './TransactionExport/TransactionExportModal';

import { trackClick } from '../../features/track';
import { FlashProvider } from '../../Shared/contexts/FlashContext';
import { urlBuilder } from '../../Shared/urlBuilder';
import { UnformattedTransactionsFilterParams } from '../data';

export interface Props {
  isLoading: boolean;
  filterParams: UnformattedTransactionsFilterParams;
}

interface LinkProps {
  name: 'Export transactions' | 'View statements';
}

const LinkContents: React.FC<React.PropsWithChildren<LinkProps>> = ({ name }) => {
  return (
    <Flex role="listitem" justifyContent="space-between" alignItems="flex-start" pt="3" width="100%">
      <Flex alignItems="flex-start">
        <Box
          aria-label={name}
          css={{
            marginRight: 'space.150',
          }}
        >
          {name === 'Export transactions' && <Icon name="Share" size="small" color="icon" />}
          {name === 'View statements' && <Icon name="DocumentPopulated" size="small" color="icon" />}
        </Box>
        <Text
          as="p"
          css={{
            paddingBottom: 'space.50',
            margin: 'space.0',
            textAlign: 'left',
          }}
        >
          {name}
        </Text>
      </Flex>
      <Box
        css={{
          paddingBottom: 'space.compat-2',
        }}
      >
        <Icon name="CaretRight" size="small" color="icon" />
      </Box>
    </Flex>
  );
};

const ManageTransactions: React.FC<React.PropsWithChildren<Props>> = ({ isLoading, filterParams }) => {
  const [shouldShowExportConfirmationModal, setShowExportConfirmationModal] = useState(false);
  const [shouldShowExportSuccessBanner, setShowExportSuccessBanner] = useState(false);
  const [shouldShowExportFailureBanner, setShowExportFailureBanner] = useState(false);

  const toggleShowExportModal = () => {
    setShowExportConfirmationModal(!shouldShowExportConfirmationModal);
  };

  return (
    <>
      {shouldShowExportConfirmationModal && (
        <FlashProvider>
          <TransactionExportModal
            filterParams={filterParams}
            onClose={toggleShowExportModal}
            setShowExportSuccessBanner={setShowExportSuccessBanner}
            setShowExportFailureBanner={setShowExportFailureBanner}
          />
        </FlashProvider>
      )}
      {shouldShowExportSuccessBanner && <ExportBanner type="success" />}
      {shouldShowExportFailureBanner && <ExportBanner type="error" />}
      <Box
        css={{
          background: 'white',
        }}
      >
        <Box
          css={{
            paddingBlock: 'space.300',
            paddingInline: 'space.300',
            borderBottom: '1px solid',
            borderColor: 'grey.20',
            verticalAlign: 'center',
          }}
        >
          <Text
            as="p"
            css={{
              fontWeight: 'bold',
              margin: 'space.0',
            }}
          >
            Manage Transactions
          </Text>
        </Box>

        <Flex role="list" py="6" px="8" flexDirection="column" justifyContent="space-between">
          {isLoading ? (
            <LinkContents name="Export transactions" />
          ) : (
            <Button
              variant="textPrimary"
              title="Export transactions"
              onClick={() => {
                trackClick({ name: 'Export transactions', module: 'ManageTransactions' });
                toggleShowExportModal();
              }}
              css={{ width: '100%' }}
            >
              <LinkContents name="Export transactions" />
            </Button>
          )}
          <Link
            href={urlBuilder.getDocuments.absolute()}
            title="View statements"
            onClick={() => trackClick({ name: 'ViewStatements', module: 'ManageTransactions' })}
          >
            <LinkContents name="View statements" />
          </Link>
        </Flex>
      </Box>
    </>
  );
};

export default ManageTransactions;
